import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useDataProvider } from 'react-admin';
import { CircularProgress, Box, Typography } from '@mui/material';

interface ActivityData {
  date: string;
  bets_count: number;
  wins: number;
  losses: number;
}

const API_URL = import.meta.env.VITE_API_URL || '/admin/api';

export const UserActivityChart = ({ userId }: { userId: number }) => {
  const [data, setData] = useState<ActivityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    // Direct fetch to avoid double userId in URL
    fetch(`${API_URL}/users/${userId}/activity`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    })
      .then((response) => response.json())
      .then((result: any) => {
        setData(result.data || []);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('Failed to load user activity:', err);
        setError('Failed to load activity data');
        setLoading(false);
      });
  }, [userId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300,
        }}
      >
        <Typography color="text.secondary">No activity data available</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
        <XAxis dataKey="date" stroke="#999" />
        <YAxis stroke="#999" />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1e1e1e',
            border: '1px solid #333',
          }}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="bets_count"
          stroke="#90caf9"
          name="Bets"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="wins"
          stroke="#4caf50"
          name="Wins"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="losses"
          stroke="#f44336"
          name="Losses"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
